import { formatInTimeZone } from 'date-fns-tz';
import { useTheme, Box, Typography, Paper, IconButton, Tooltip, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const formatDate = (val) => {
    if (!val) return '—';
    try { return formatInTimeZone(val, 'UTC', 'HH:mm dd/MM/yyyy'); } catch { return '—'; }
};

const EntryCard = ({ row, index, canEdit, canDelete, onEdit, onDelete }) => {
    const theme = useTheme();
    const crewMembers = Array.isArray(row.crewMembers)
        ? [...row.crewMembers].sort((a, b) => String(a.crewCode).localeCompare(String(b.crewCode)))
        : [];

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold" flex={1} noWrap>
                    {row.destination || '—'}{row.travelZone ? ` · ${row.travelZone}` : ''}
                </Typography>
                {row.isInternational && (
                    <Chip label="Int'l" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {canEdit && (
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(row._id)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canDelete && (
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDelete(row._id)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}>
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {formatDate(row.startDate)} → {formatDate(row.endDate)}
            </Typography>

            <Box display="flex" gap={2} mt={0.75} flexWrap="wrap">
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">TravelHours</Typography>
                    <Typography variant="body2" fontWeight="medium">{row.travelHours ?? '—'}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Perdiem</Typography>
                    <Typography variant="body2" fontWeight="medium">{row.perdiem ?? '—'}</Typography>
                </Box>
                {row.status && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                        <Typography variant="body2" fontWeight="medium">{row.status}</Typography>
                    </Box>
                )}
            </Box>

            {row.description && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {row.description}
                </Typography>
            )}

            {crewMembers.length > 0 && (
                <Box mt={0.75}>
                    <Typography variant="caption" color="text.secondary">
                        Crew: <strong>{crewMembers.length}</strong> members
                    </Typography>
                    <Box mt={0.25}>
                        {crewMembers.map((member, i) => (
                            <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" sx={{ minWidth: 32, fontWeight: 'bold' }}>
                                    {member.crewCode}
                                </Typography>
                                <Typography variant="caption" flex={1}>{member.crewName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ({member.rank} | {member.base})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};


const TravelAllowanceEntriesList = ({ rows, canEdit, canDelete, onEdit, onDelete }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) => (
                <EntryCard
                    key={row._id}
                    row={row}
                    index={index}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            ))}
        </Box>
    );
};

export default TravelAllowanceEntriesList;
